import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { FloatingSocials } from '../components/FloatingSocials';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Landing = () => {
  const { lang, setLang } = useLanguage();
  const cacheKey = `landing_content_${lang}`;
  const [content, setContent] = useState(() => {
    try { const cached = localStorage.getItem(cacheKey); return cached ? JSON.parse(cached) : null; }
    catch { return null; }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '', email: '', phone: '', restaurant_name: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    // Try loading cached content for the new lang instantly, then refresh in background
    try {
      const cached = localStorage.getItem(`landing_content_${lang}`);
      if (cached) setContent(JSON.parse(cached));
    } catch {}
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const fetchContent = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/site/content`, { params: { lang } });
      setContent(data);
      try { localStorage.setItem(`landing_content_${lang}`, JSON.stringify(data)); } catch {}
      if (data?.browser_title) {
        document.title = data.browser_title;
      }
    } catch (err) {
      console.error('Content load error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/contact`, contactForm);
      setSubmitStatus({
        type: 'success',
        text: data?.message || 'Mesajınız uğurla göndərildi! Tezliklə sizinlə əlaqə saxlayacağıq.',
      });
      setContactForm({ name: '', email: '', phone: '', restaurant_name: '', message: '' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      let text = 'Xəta baş verdi. Lütfən yenidən cəhd edin.';
      if (typeof detail === 'string') {
        text = detail;
      } else if (detail && typeof detail === 'object' && detail.message) {
        text = detail.message;
      }
      setSubmitStatus({ type: 'error', text });
    } finally {
      setSubmitting(false);
    }
  };

  if (!content) {
    // Lightweight skeleton (only shown on first-ever visit before cache exists).
    // Structure mirrors real layout so no CLS shift when data arrives.
    return (
      <div className="bg-stone-50 min-h-screen">
        <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-lg border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl animate-pulse"></div>
              <div className="h-5 w-32 bg-stone-200 rounded animate-pulse"></div>
            </div>
            <div className="h-9 w-24 bg-emerald-100 rounded-full animate-pulse"></div>
          </div>
        </nav>
        <div className="pt-24 pb-32 px-6 max-w-4xl mx-auto text-center">
          <div className="h-16 w-3/4 mx-auto bg-stone-200 rounded animate-pulse mb-6"></div>
          <div className="h-6 w-2/3 mx-auto bg-stone-200 rounded animate-pulse mb-3"></div>
          <div className="h-6 w-1/2 mx-auto bg-stone-200 rounded animate-pulse mb-10"></div>
          <div className="flex justify-center gap-3">
            <div className="h-12 w-40 bg-emerald-200 rounded-xl animate-pulse"></div>
            <div className="h-12 w-40 bg-stone-200 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  const c = content;

  return (
    <div className="bg-stone-50">
      {/* NAVBAR */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-lg border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center space-x-2 hover:opacity-80 transition" data-testid="logo-home-link">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-lg">
                {c.site_name?.[0] || 'S'}
              </div>
              <span className="text-base sm:text-xl font-bold text-stone-900">{c.site_name}<span className="text-emerald-600">{c.site_tld}</span></span>
            </a>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-stone-700 hover:text-emerald-600 transition font-medium">{c.nav_features}</a>
              <a href="#how-it-works" className="text-stone-700 hover:text-emerald-600 transition font-medium">{c.nav_how}</a>
              <a href="#contact" className="text-stone-700 hover:text-emerald-600 transition font-medium">{c.nav_contact}</a>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Language switcher (flags) — visible on all sizes */}
              <div className="flex items-center gap-0.5 sm:gap-1 border border-stone-200 rounded-full p-0.5 sm:p-1 bg-white/60" data-testid="lang-switcher">
                {[
                  { code: 'az', flag: '🇦🇿', label: 'AZ' },
                  { code: 'tr', flag: '🇹🇷', label: 'TR' },
                  { code: 'en', flag: '🇬🇧', label: 'EN' },
                  { code: 'ru', flag: '🇷🇺', label: 'RU' },
                ].map(({ code, flag, label }) => (
                  <button
                    key={code}
                    onClick={() => setLang(code)}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-base sm:text-lg transition ${
                      lang === code
                        ? 'bg-emerald-600 shadow ring-2 ring-emerald-200'
                        : 'hover:bg-stone-100'
                    }`}
                    title={label}
                    aria-label={`Switch to ${label}`}
                    data-testid={`lang-flag-${code}`}
                  >
                    <span>{flag}</span>
                  </button>
                ))}
              </div>

              <div className="relative group hidden sm:block" data-testid="auth-hover-group">
                <a href="/login" className="inline-block text-stone-700 group-hover:text-emerald-600 px-3 py-2 rounded-full font-medium transition" data-testid="nav-login">
                  Giriş
                </a>
                {/* Hover popover: Qeydiyyat */}
                <div className="absolute right-0 top-full pt-1 hidden group-hover:block">
                  <a
                    href="/register"
                    className="block whitespace-nowrap bg-white border border-stone-200 shadow-xl rounded-xl px-4 py-2 text-stone-700 hover:text-emerald-700 hover:border-emerald-300 font-medium transition text-sm"
                    data-testid="nav-register"
                  >
                    ➕ Qeydiyyat
                  </a>
                </div>
              </div>
              <a href={c.nav_cta_url || '#contact'} className="hidden sm:inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-full font-medium transition shadow-md hover:shadow-lg" data-testid="nav-contact">
                {c.nav_cta}
              </a>
              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-stone-100 transition"
                aria-label="Menyu"
                data-testid="mobile-menu-toggle"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-stone-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  {mobileMenuOpen
                    ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile dropdown menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-3 border-t border-stone-200 space-y-1" data-testid="mobile-menu-panel">
              <a
                onClick={() => setMobileMenuOpen(false)}
                href="#features"
                className="block px-3 py-2.5 rounded-lg text-stone-700 hover:bg-emerald-50 hover:text-emerald-700 font-medium transition"
              >
                {c.nav_features}
              </a>
              <a
                onClick={() => setMobileMenuOpen(false)}
                href="#how-it-works"
                className="block px-3 py-2.5 rounded-lg text-stone-700 hover:bg-emerald-50 hover:text-emerald-700 font-medium transition"
              >
                {c.nav_how}
              </a>
              <a
                onClick={() => setMobileMenuOpen(false)}
                href="#contact"
                className="block px-3 py-2.5 rounded-lg text-stone-700 hover:bg-emerald-50 hover:text-emerald-700 font-medium transition"
              >
                {c.nav_contact}
              </a>
              <div className="pt-2 border-t border-stone-100 grid grid-cols-2 gap-2 mt-2">
                <a
                  href="/login"
                  className="text-center px-3 py-2.5 rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 font-medium transition"
                  data-testid="mobile-nav-login"
                >
                  Giriş
                </a>
                <a
                  href="/register"
                  className="text-center px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition"
                  data-testid="mobile-nav-register"
                >
                  Qeydiyyat
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${c.hero_bg_url})` }}></div>
          <div className="absolute inset-0 bg-gradient-to-br from-stone-900/95 via-stone-900/90 to-emerald-950/90"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <div className="inline-flex items-center space-x-2 bg-emerald-500/20 backdrop-blur-sm border border-emerald-400/30 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              <span className="text-sm text-emerald-200">{c.hero_badge}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              {c.hero_title_line1}
              <span className="block bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
                {c.hero_title_line2}
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-stone-300 mb-8 leading-relaxed">
              {c.hero_subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={c.hero_cta_url || '#contact'} className="inline-flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-4 rounded-full shadow-2xl shadow-emerald-500/30 transition transform hover:scale-105" data-testid="hero-cta-contact">
                {c.hero_cta_text}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              {c.hero_secondary_enabled && c.hero_secondary_text && (
                <a href={c.hero_secondary_url || '#features'} className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-bold px-8 py-4 rounded-full border border-white/20 transition" data-testid="hero-cta-secondary">
                  {c.hero_secondary_text}
                </a>
              )}
            </div>
            <div className="flex items-center gap-8 mt-12">
              {c.hero_stats?.map((stat, i) => (
                <div key={i}>
                  <p className="text-3xl font-bold text-amber-400">{stat.value}</p>
                  <p className="text-sm text-stone-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Phone mockup */}
          <div className="relative hidden lg:block">
            <div className="relative mx-auto w-72">
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500 to-amber-500 rounded-[3rem] blur-3xl opacity-30"></div>
              <div className="relative bg-stone-900 rounded-[3rem] p-3 shadow-2xl border-4 border-stone-700">
                <div className="bg-white rounded-[2.5rem] overflow-hidden aspect-[9/19]">
                  <div className="h-32 bg-gradient-to-br from-amber-700 to-stone-800 relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="text-white font-bold text-lg">{c.mockup_restaurant_name}</p>
                      <p className="text-amber-300 text-xs">{c.mockup_subtitle}</p>
                    </div>
                  </div>
                  <div className="p-3 space-y-2">
                    {[
                      { name: 'KEBABLAR', count: 4, color: 'from-amber-600 to-amber-800' },
                      { name: 'STEAK', count: 2, color: 'from-red-700 to-red-900' },
                      { name: 'PİZZALAR', count: 2, color: 'from-orange-600 to-red-700' },
                    ].map((cat, i) => (
                      <div key={i} className={`bg-gradient-to-br ${cat.color} rounded-xl p-3 text-white shadow-md`}>
                        <p className="font-bold text-sm">{cat.name}</p>
                        <p className="text-xs opacity-80">{cat.count} Məhsul</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 animate-bounce">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold uppercase tracking-wider text-sm">{c.features_subtitle}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mt-3 mb-4">
              {c.features_title_part1} <span className="text-emerald-600">{c.features_title_highlight}</span>
            </h2>
            <p className="text-stone-600 text-lg max-w-2xl mx-auto">{c.features_description}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.features?.map((feat, idx) => (
              <div key={idx} className="group bg-white rounded-2xl p-8 border border-stone-200 hover:border-emerald-300 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1" data-testid={`feature-${idx}`}>
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feat.color} flex items-center justify-center text-2xl shadow-lg mb-4 group-hover:scale-110 transition`}>
                  {feat.icon}
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-2">{feat.title}</h3>
                <p className="text-stone-600 leading-relaxed">{feat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 lg:py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold uppercase tracking-wider text-sm">{c.how_it_works_subtitle}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mt-3 mb-4">{c.how_it_works_title}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {c.how_it_works_steps?.map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition h-full">
                  <div className="text-6xl font-bold text-emerald-100 mb-4">{step.step}</div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">{step.title}</h3>
                  <p className="text-stone-600">{step.description}</p>
                </div>
                {i < c.how_it_works_steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-emerald-400">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold uppercase tracking-wider text-sm">{c.contact_subtitle}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mt-3 mb-4">{c.contact_title}</h2>
            <p className="text-stone-600 text-lg max-w-2xl mx-auto">{c.contact_description}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div className="bg-stone-50 rounded-3xl p-8 lg:p-10">
              <h3 className="text-2xl font-bold text-stone-900 mb-6">Mesaj göndər</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Ad Soyad *</label>
                  <input type="text" required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="w-full px-4 py-3 bg-white border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition" placeholder="Adınızı yazın" data-testid="contact-name-input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Email *</label>
                    <input type="email" required value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="w-full px-4 py-3 bg-white border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition" placeholder="email@example.com" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Telefon</label>
                    <input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="w-full px-4 py-3 bg-white border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition" placeholder="+994XX XXX XX XX" data-testid="contact-phone-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Restoran adı</label>
                  <input type="text" value={contactForm.restaurant_name} onChange={(e) => setContactForm({ ...contactForm, restaurant_name: e.target.value })} className="w-full px-4 py-3 bg-white border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition" placeholder="Restoranınızın adı" data-testid="contact-restaurant-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Mesaj *</label>
                  <textarea required value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} className="w-full px-4 py-3 bg-white border border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition resize-none" rows="4" placeholder="Necə kömək edə bilərik?" data-testid="contact-message-input"></textarea>
                </div>

                {submitStatus && (
                  <div className={`p-3 rounded-xl text-sm ${submitStatus.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="contact-status">
                    {submitStatus.text}
                  </div>
                )}

                <button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl transition shadow-lg hover:shadow-xl disabled:opacity-50" data-testid="contact-submit-button">
                  {submitting ? 'Göndərilir...' : 'Mesajı göndər'}
                </button>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-3xl p-8 lg:p-10 text-white shadow-2xl">
                <h3 className="text-2xl font-bold mb-6">Əlaqə məlumatları</h3>
                <div className="space-y-5">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-emerald-100 text-sm">Email</p>
                      <p className="font-bold text-lg">{c.contact_email}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-emerald-100 text-sm">Ünvan</p>
                      <p className="font-bold text-lg">{c.contact_address}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 pt-6 border-t border-white/20">
                  <p className="text-emerald-100 text-sm leading-relaxed">{c.contact_info_note}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-stone-900 text-stone-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                  {c.site_name?.[0] || 'S'}
                </div>
                <span className="text-xl font-bold text-white">{c.site_name}<span className="text-emerald-500">{c.site_tld}</span></span>
              </div>
              <p className="text-sm">{c.footer_tagline}</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Sürətli Linklər</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-emerald-400 transition">{c.nav_features}</a></li>
                <li><a href="#how-it-works" className="hover:text-emerald-400 transition">{c.nav_how}</a></li>
                <li><a href="#contact" className="hover:text-emerald-400 transition">{c.nav_contact}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Əlaqə</h4>
              <ul className="space-y-2 text-sm">
                <li>📧 {c.contact_email}</li>
                <li>📍 {c.contact_address}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-6 flex flex-col sm:flex-row justify-between items-center text-sm">
            <p>{c.footer_copyright}</p>
            <p className="mt-2 sm:mt-0">{c.footer_made_in}</p>
          </div>
        </div>
      </footer>
      <FloatingSocials />
    </div>
  );
};
